"use client";

import React from "react";
import { CircleAlert, ShieldAlert, Zap, ArrowRight, Layers3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const policySteps = [{
  label: "80%",
  title: "Warning",
  description: "When usage reaches 80%, EloRAG shows a warning so teams can review capacity before service is affected.",
  icon: CircleAlert,
  iconWrap: "bg-amber-500/10 text-amber-600",
  border: "border-amber-500/20"
}, {
  label: "100%",
  title: "Usage blocked",
  description: "When a plan limit is reached, additional usage is blocked. EloRAG does not fall back to platform keys or hidden overages.",
  icon: ShieldAlert,
  iconWrap: "bg-destructive/10 text-destructive",
  border: "border-destructive/20"
}, {
  label: "Fix",
  title: "Upgrade or add capacity",
  description: "Service can continue by upgrading the subscription plan or adding a Capacity Pack for the relevant resource type.",
  icon: Zap,
  iconWrap: "bg-primary/10 text-primary",
  border: "border-primary/20",
  action: {
    label: "View Capacity Packs",
    href: "/CapacityPacksPage"
  }
}];
export default function OverLimitPolicy() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="over-limit-policy" className="bg-background py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={cn("mx-auto max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            Over-limit policy
          </div>

          <p className="mt-2 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Hard caps. Clear behavior.
          </p>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            EloRAG uses hard backend-enforced caps to keep platform behavior predictable.
            No unlimited usage, no silent overages, and no fallback platform keys.
          </p>
        </div>

        <div ref={gridRef} className={cn("mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-3 transition-all duration-700 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {policySteps.map((step, i) => {
          const Icon = step.icon;
          return <Card key={step.title} data-index={i} className="flex h-full flex-col rounded-2xl border border-border/70 bg-card p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)] transition-all duration-300" style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <div className="mb-5 flex items-center justify-between">
                  <div className={cn("rounded-xl p-3", step.iconWrap)}>
                    <Icon className="size-5" />
                  </div>

                  <div className={cn("rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground", step.border)}>
                    {step.label}
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-foreground">{step.title}</h3>
                <p className="mb-6 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {step.description}
                </p>

                {step.action && <div className="mt-auto">
                    <Button asChild className="h-11" size="lg">
                      <Link to={step.action.href}>
                        {step.action.label}
                        <ArrowRight className="ml-2 size-4" />
                      </Link>
                    </Button>
                  </div>}
              </Card>;
        })}
        </div>

        <div className="mx-auto mt-8 max-w-4xl rounded-xl border border-border/70 bg-secondary/25 p-4 text-center">
          <p className="text-sm leading-relaxed text-muted-foreground">
            At 80% usage you get a warning. At 100% usage service is blocked. To continue,
            upgrade the plan or add a Capacity Pack.
          </p>
        </div>
      </div>
    </section>;
}